import { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Grid2 as Grid,
    Typography,
    Divider,
    TextField,
    Button,
    MenuItem,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { useUser } from '../../../hooks/useUser';
import { useStressTestRanges } from '../../../hooks/useClinicSettings';
import {
    useCreateStressTest,
    useUploadStressTestFile
} from '../../../hooks/useStressTest';
import { generateStressTestPDF } from './stressTestPdfGenerator';
import { MainCard } from 'component/cards/MainCard2';

const calculateAge = (birthDate) => {
    if (!birthDate) return '';
    const today = new Date();
    const birth = new Date(birthDate);

    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
    }

    return age;
};

const calculateBMI = (weight, height) => {
    if (!weight || !height) return '';
    const h = height / 100;
    return (weight / (h * h)).toFixed(1);
};

const classifyValue = (value, field, ranges) => {
    if (!value || !ranges?.[field]) return null;

    const num = Number(value);
    if (isNaN(num)) return null;

    const { normal, mild, severe } = ranges[field];

    if (num >= normal.min && num <= normal.max) return 'normal';
    if (num >= mild.min && num <= mild.max) return 'mild';
    if (num >= severe.min && num <= severe.max) return 'severe';

    return null;
};

const generateEffortReport = (results, ranges) => {
    const protocolo = results['Protocolo'] || 'XXXXX';
    const repouso = results['Repouso'] || 'XXXXX';
    const tempo = results['Tempo'] || 'XX:XX';
    const suspensao = results['Suspensão por'] || 'XXXXXX';
    const picoFc = results['Pico FC'] || 'XXX';
    const mets = results['METS'] || 'XXX';
    const altST = results['Alt. Seg. ST'];
    const angina = results['Angina'];

    const metsClass = classifyValue(mets, 'METS', ranges);

    let capacityText = 'Boa capacidade funcional';
    if (metsClass === 'mild')
        capacityText = 'Capacidade funcional moderadamente reduzida';
    if (metsClass === 'severe')
        capacityText = 'Capacidade funcional severamente reduzida';

    const stText = altST
        ? 'Com alterações ST-T significativas.'
        : 'Sem alterações ST-T significativas.';
    const anginaText = angina ? 'Com angor.' : 'Sem angor.';

    const ischemiaConclusion =
        altST || angina
            ? 'Prova de esforço positiva para isquémia miocárdica.'
            : 'Prova de esforço negativa para isquémia miocárdica.';

    return `ECG basal: ${repouso}
- Prova de esforço em tapete rolante efetuada segundo o protocolo de ${protocolo}, máxima tendo atingido ${picoFc} bpm da FC máx. prevista (100%) sob terapêutica beta-bloqueante, interrompida aos ${tempo} minutos por ${suspensao}.
- ${capacityText} (${mets} METs).
- Respostas cronotrópica e tensional adequada ao esforço.
- ${stText}
- ${anginaText}

Conclusão:
- Excelente tolerância ao esforço.
- ${ischemiaConclusion}`;
};

const generateCardioRespiratoryReport = (results, ranges) => {
    const protocolo = results['Protocolo'] || 'XXXXX';
    const picoFc = results['Pico FC'] || 'XX';
    const tempo = results['Tempo'] || 'XX:XX';
    const suspensao = results['Suspensão por'] || 'XXXXXX';
    const fcmPrev = results['% FC prevista'] || 'XX';
    const vo2 = results['VO2'] || 'XXX';
    const vo2Percent = results['% previsto'] || 'XX';
    const o2Pulse = results['O2 pulso % prev.'] || 'XXX';
    const vt1 = results['VT1%picoVO2'] || 'XX';
    const vevco2 = results['VE/VCO2'] || 'XX';

    const vo2Class = classifyValue(vo2, 'VO2', ranges);
    const veClass = classifyValue(vevco2, 'VE/VCO2', ranges);

    const functionalText =
        vo2Class === 'normal'
            ? 'ausência de limitação da capacidade funcional'
            : vo2Class === 'mild'
              ? 'limitação moderada da capacidade funcional'
              : 'limitação grave da capacidade funcional';

    const ventilatoryText =
        veClass === 'normal'
            ? 'Sem achados sugestivos de hipertensão pulmonar com o esforço'
            : 'Com critérios de hipertensão pulmonar com o esforço';

    return `ECG basal em ritmo sinusal. Ausência de arritmias significativas.
- Prova de esforço em tapete rolante efetuada segundo o protocolo de ${protocolo} XX-XX-XX, com a finalidade de XXXXX,
interrompida aos ${tempo} minutos por ${suspensao} , tendo atingido uma frequência cardíaca máxima de ${picoFc} bpm (${fcmPrev}% FMCPI)
sob terapêutica beta-bloqueante (XXXX, XXmg).
- Prova máxima por quociente respiratório (QR>1.10), demonstrando ${functionalText}
(VO2 pico de ${vo2} mL/kg/min que corresponde a ${vo2Percent}% do previsto) e O2 pulso de ${o2Pulse}% normal.
- Ausência de achados sugestivos de limitação circulatória (VT1 ${vt1}% do pVO2 previsto e O2 pulso normal).
- ${ventilatoryText} (rampa VE/VCO2 ${vevco2}).
- Sem alteração ventilatória na espirometria.
- Sem evidência de isquémia por critérios clínicos ou eletrocardiográficos.
- Resposta tensional e cronotrópica com o exercício

Conclusão: Prova de esforço máxima. ${functionalText}.`;
};

const generateDynamicReport = (testType, results, ranges) => {
    if (!ranges) return '';

    if (testType === 'STRESS_TEST') {
        return generateEffortReport(results, ranges);
    }

    return generateCardioRespiratoryReport(results, ranges);
};

const calculateChronotropicIndex = (picoFC, repousoFC, age) => {
    const pico = Number(picoFC);
    const repouso = Number(repousoFC);
    const idade = Number(age);

    if (isNaN(pico) || isNaN(repouso) || isNaN(idade) || idade <= 0) return '';

    const maxPredicted = 220 - idade;
    const denominator = maxPredicted - repouso;

    if (denominator <= 0) return '';

    return ((pico - repouso) / denominator).toFixed(2);
};

const CreateStressTest = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { patient, from, seeUserState } = location.state || {};
    const { user } = useUser();
    const ranges = useStressTestRanges();
    const { mutateAsync: uploadFile } = useUploadStressTestFile();

    const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
    const [openErrorDialog, setOpenErrorDialog] = useState(false);

    const { mutateAsync: createStressTest, isPending } = useCreateStressTest();

    const [testType, setTestType] = useState('STRESS_TEST');
    const [testProtocolType, setTestProtocolType] = useState('TAPETE_ROLANTE');

    const [medication, setMedication] = useState({
        betaBlocker: { enabled: false, type: '' },
        calciumBlocker: { enabled: false, type: '' },
        digitalis: { enabled: false, type: '' },
        antiarrhythmic: { enabled: false, type: '' }
    });

    const MEDICATION_GROUPS = [
        {
            key: 'betaBlocker',
            label: 'Betabloqueantes',
            options: [
                'Bisoprolol',
                'Metoprolol',
                'Carvedilol',
                'Propranolol',
                'Atenolol',
                'Nebivolol',
                'Outro'
            ]
        },
        {
            key: 'calciumBlocker',
            label: 'Bloqueadores dos canais de cálcio',
            options: ['Verapamil', 'Diltiazem', 'Outro']
        },
        {
            key: 'digitalis',
            label: 'Digitálicos',
            options: ['Digoxina', 'Outro']
        },
        {
            key: 'antiarrhythmic',
            label: 'Antiarrítmicos',
            options: ['Amiodarona', 'Sotalol', 'Outro']
        }
    ];

    const [identification, setIdentification] = useState({
        name: patient?.name || '',
        sex: patient?.sex || '',
        birthdate: patient?.birth_date || '',
        age: calculateAge(patient?.birth_date),
        weight: patient?.weight || '',
        height: patient?.height || '',
        bmi: calculateBMI(patient?.weight, patient?.height),
        processNumber: patient?.health_id || ''
    });

    const [generalData, setGeneralData] = useState({
        origin: user?.selectedClinic?.name || '',
        doctor: user?.name || '',
        operators: ''
    });

    const [clinicalInfo, setClinicalInfo] = useState('');

    const [results, setResults] = useState({});

    const [report, setReport] = useState('');

    const [isReportLocked, setIsReportLocked] = useState(false);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    const RESULT_FIELDS = useMemo(() => {
        if (testType === 'STRESS_TEST_CP') {
            return [
                {
                    title: 'PE Clássica',
                    fields: [
                        'Protocolo',
                        'Tempo',
                        'Suspensão por',
                        'PA repouso',
                        'PA esforço',
                        'Extrassístoles V',
                        'Angina',
                        'Alt. Seg. ST',
                        'METS'
                    ]
                },
                {
                    title: 'PERC',
                    fields: [
                        'VO2',
                        'Pico',
                        '% previsto',
                        'Repouso',
                        'VT1',
                        'VT1%picoVO2',
                        'VT2',
                        'Quociente Resp.',
                        'O2 pulso % prev.'
                    ]
                },
                {
                    title: 'Ventilação',
                    fields: [
                        'FVC',
                        'FEV1',
                        'FEV1/FVC',
                        'Res. Respiratória',
                        'Osc. Ventilatória',
                        'VE/VCO2',
                        'EqCO2 VT1',
                        'PETCO2 VT1',
                        'ΔPETCO2'
                    ]
                },
                {
                    title: 'Cronotropia e Treino',
                    fields: [
                        'Repouso',
                        'VT1',
                        'VT2',
                        'FCMPI',
                        'Pico FC',
                        '% FC prevista',
                        '1º min Recup',
                        '3º min Recup',
                        'Índice cronotrópico'
                    ]
                }
            ];
        }

        return [
            {
                title: 'Sub-secção 1',
                fields: [
                    'Protocolo',
                    'Tempo',
                    'Suspensão por',
                    'PAS repouso',
                    'PAS esforço'
                ]
            },
            {
                title: 'Sub-secção 2',
                fields: [
                    'Repouso',
                    'Pico FC',
                    'FCMPI',
                    '% FC prevista',
                    'Índice cronotrópico'
                ]
            },
            {
                title: 'Sub-secção 3',
                fields: [
                    'Extrassístoles SV',
                    'Extrassístoles V',
                    'Angina',
                    'Alt. Seg. ST'
                ]
            },
            {
                title: 'Sub-secção 4',
                fields: ['METS', '1º min Recup', '3º min Recup', '6º min Recup']
            }
        ];
    }, [testType]);

    useEffect(() => {
        setIdentification((prev) => ({
            ...prev,
            bmi: calculateBMI(prev.weight, prev.height)
        }));
    }, [identification.weight, identification.height]);

    useEffect(() => {
        if (isReportLocked) return;

        const dynamicReport = generateDynamicReport(testType, results, ranges);
        setReport(dynamicReport);
    }, [testType, results, ranges, isReportLocked]);

    useEffect(() => {
        const chronotropicIndex = calculateChronotropicIndex(
            results['Pico FC'],
            results['Repouso'],
            identification.age
        );

        if (chronotropicIndex !== '') {
            setResults((prev) => ({
                ...prev,
                'Índice cronotrópico': chronotropicIndex
            }));
        }
    }, [results['Pico FC'], results['Repouso'], identification.age]);

    const getFieldColor = (field, value) => {
        if (!value || !ranges?.[field]) return 'transparent';

        const num = Number(value);
        if (isNaN(num)) return 'transparent';

        const { normal, mild, severe } = ranges[field];

        if (num >= normal.min && num <= normal.max) return '#abffae';
        if (num >= mild.min && num <= mild.max) return '#fff594';
        if (num >= severe.min && num <= severe.max) return '#fb9fae';

        return 'transparent';
    };

    const handleSubmit = async () => {
        try {
            const resp = await createStressTest({
                patient: patient?.role_uuid,
                doctor: user?.selectedRole,
                caregiver: null,
                type: testType,
                ts: new Date().toISOString(),
                data: {
                    testProtocolType,
                    identification,
                    generalData,
                    clinicalInfo,
                    medication,
                    results,
                    report
                }
            });

            const testUuid = resp.data.uuid;
            const pdfBlob = await generateStressTestPDF({
                testType,
                identification,
                generalData,
                clinicalInfo,
                results,
                report,
                ranges,
                download: false
            });

            const pdfFile = new File(
                [pdfBlob],
                testType === 'STRESS_TEST_CP'
                    ? 'prova_esforco_cardiorrespiratoria.pdf'
                    : 'prova_esforco.pdf',
                { type: 'application/pdf' }
            );

            await uploadFile({
                testUuid,
                file: pdfFile
            });

            setOpenSuccessDialog(true);
        } catch (error) {
            console.error('Erro ao criar prova de esforço:', error);
            setOpenErrorDialog(true);
        }
    };

    const handleBack = () => {
        if (from) {
            navigate(from.pathname, {
                state: {
                    ...from.state,
                    restoredState: seeUserState
                }
            });
        } else {
            navigate(-1);
        }
    };

    return (
        <MainCard title={t('stress.title')}>
            <Grid container spacing={3}>
                {/* Tipo de Prova */}
                <Grid size={6}>
                    <TextField
                        select
                        fullWidth
                        label={t('stress.type')}
                        value={testType}
                        onChange={(e) => {
                            setTestType(e.target.value);
                        }}
                    >
                        <MenuItem value="STRESS_TEST">{t('stress.title2')}</MenuItem>
                        <MenuItem value="STRESS_TEST_CP">{t('stress.title3')}</MenuItem>
                    </TextField>
                </Grid>
                {/* Tipo de Protocolo */}
                <Grid size={6}>
                    <TextField
                        select
                        fullWidth
                        label={t('stress.protocolType')}
                        value={testProtocolType}
                        onChange={(e) => {
                            setTestProtocolType(e.target.value);
                        }}
                    >
                        <MenuItem value="TAPETE_ROLANTE">{t('stress.roler')}</MenuItem>
                        <MenuItem value="CICLOERGOMETRO">{t('stress.bike')}</MenuItem>
                    </TextField>
                </Grid>

                {/* IDENTIFICAÇÃO */}
                <Grid size={12}>
                    <Typography variant="h5">{t('stress.identification')}</Typography>
                </Grid>
                {Object.entries(identification).map(([key, value]) => (
                    <Grid key={key} size={4}>
                        <TextField
                            fullWidth
                            label={key}
                            value={value}
                            disabled={
                                key === 'name' ||
                                key === 'sex' ||
                                key === 'birthdate' ||
                                key === 'age' ||
                                key === 'bmi'
                            }
                            onChange={(e) =>
                                setIdentification((prev) => ({
                                    ...prev,
                                    [key]: e.target.value
                                }))
                            }
                        />
                    </Grid>
                ))}

                <Divider flexItem />

                {/* DADOS GERAIS */}
                <Grid size={12}>
                    <Typography variant="h5">{t('stress.data')}</Typography>
                </Grid>
                {Object.entries(generalData).map(([key, value]) => (
                    <Grid key={key} size={4}>
                        <TextField
                            fullWidth
                            label={key}
                            value={value}
                            onChange={(e) =>
                                setGeneralData((prev) => ({
                                    ...prev,
                                    [key]: e.target.value
                                }))
                            }
                        />
                    </Grid>
                ))}

                <Divider flexItem />

                {/* INFORMAÇÃO CLÍNICA */}
                <Grid size={12}>
                    <Typography variant="h5">{t('stress.info')}</Typography>
                </Grid>
                <Grid size={12}>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label={t('stress.indication')}
                        value={clinicalInfo}
                        onChange={(e) => setClinicalInfo(e.target.value)}
                    />
                </Grid>

                <Divider flexItem />

                {/* MEDICAÇÃO */}
                <Grid size={12}>
                    <Typography variant="h5">Medicação</Typography>
                </Grid>

                <Grid container spacing={3}>
                    {MEDICATION_GROUPS.map((group) => (
                        <Grid key={group.key} size={3}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={medication[group.key].enabled}
                                        onChange={(e) =>
                                            setMedication((prev) => ({
                                                ...prev,
                                                [group.key]: {
                                                    enabled: e.target.checked,
                                                    type: e.target.checked
                                                        ? prev[group.key].type
                                                        : ''
                                                }
                                            }))
                                        }
                                    />
                                }
                                label={group.label}
                            />

                            {medication[group.key].enabled && (
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    sx={{ mt: 1 }}
                                    label="Fármaco"
                                    value={medication[group.key].type}
                                    onChange={(e) =>
                                        setMedication((prev) => ({
                                            ...prev,
                                            [group.key]: {
                                                ...prev[group.key],
                                                type: e.target.value
                                            }
                                        }))
                                    }
                                >
                                    {group.options.map((opt) => (
                                        <MenuItem key={opt} value={opt}>
                                            {opt}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                        </Grid>
                    ))}
                </Grid>

                {/* RESULTADOS */}
                <Grid
                    size={12}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1
                    }}
                >
                    <Typography variant="h5">{t('stress.results')}</Typography>

                    {isReportLocked && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => setOpenConfirmDialog(true)}
                        >
                            Editar resultados
                        </Button>
                    )}
                </Grid>

                <Grid container spacing={2}>
                    {RESULT_FIELDS.map((section) => (
                        <Grid key={section.title} size={{ xs: 12, sm: 6, md: 3 }}>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        mb: 2,
                                        fontWeight: 600,
                                        textAlign: 'center'
                                    }}
                                >
                                    {section.title}
                                </Typography>

                                <Grid container spacing={2}>
                                    {section.fields.map((field) => (
                                        <Grid key={field} size={12}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={field}
                                                value={results[field] || ''}
                                                onChange={(e) =>
                                                    setResults((prev) => ({
                                                        ...prev,
                                                        [field]: e.target.value
                                                    }))
                                                }
                                                disabled={
                                                    isReportLocked ||
                                                    field === 'Índice cronotrópico'
                                                }
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: getFieldColor(
                                                            field,
                                                            results[field]
                                                        )
                                                    },
                                                    '& .MuiOutlinedInput-input': {
                                                        backgroundColor: getFieldColor(
                                                            field,
                                                            results[field]
                                                        )
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* Nota informativa fixa */}
                <Grid size={12}>
                    <Typography variant="caption" color="text.secondary">
                        {t('stress.note')}
                    </Typography>
                </Grid>

                <Divider flexItem />

                {/* RELATÓRIO */}
                <Grid size={12}>
                    <Typography variant="h5">{t('stress.report')}</Typography>
                </Grid>
                <Grid size={12}>
                    <TextField
                        fullWidth
                        multiline
                        minRows={10}
                        value={report}
                        onChange={(e) => {
                            setReport(e.target.value);

                            if (!isReportLocked) {
                                setIsReportLocked(true);
                            }
                        }}
                    />
                </Grid>

                {/* AÇÕES */}
                <Grid
                    size={12}
                    sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
                >
                    <Button sx={{ mr: 2 }} variant="outlined" onClick={handleBack}>
                        {t('stress.cancel')}
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isPending}
                    >
                        {t('stress.save')}
                    </Button>
                </Grid>
            </Grid>

            <Dialog
                open={openConfirmDialog}
                onClose={() => setOpenConfirmDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <WarningAmberIcon color="secondary" />
                    {t('stress.confirm')}
                </DialogTitle>

                <DialogContent dividers>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        <Trans i18nKey="stress.editWarningLine1" />
                    </Typography>

                    <Typography variant="body1">
                        <Trans i18nKey="stress.editWarningLine2" />
                    </Typography>

                    <Typography variant="body1" sx={{ mt: 3, fontWeight: 600 }}>
                        {t('stress.continue')}
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setOpenConfirmDialog(false)}
                        variant="outlined"
                    >
                        {t('stress.cancel')}
                    </Button>

                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => {
                            setIsReportLocked(false);
                            setOpenConfirmDialog(false);
                        }}
                    >
                        {t('stress.confirmEdit')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={openSuccessDialog}
                onClose={() => {
                    setOpenSuccessDialog(false);
                    navigate(from.pathname, {
                        state: {
                            ...from.state,
                            restoredState: seeUserState
                        }
                    });
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('stress.sucess')}</DialogTitle>

                <DialogContent dividers>
                    <Typography variant="body1">{t('stress.sucessMessage')}</Typography>
                </DialogContent>

                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setOpenSuccessDialog(false);
                            navigate(from.pathname, {
                                state: {
                                    ...from.state,
                                    restoredState: seeUserState
                                }
                            });
                        }}
                    >
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={openErrorDialog}
                onClose={() => setOpenErrorDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('stress.error')}</DialogTitle>

                <DialogContent dividers>
                    <Typography variant="body1">
                        {t('stress.errorMessage')}
                        <br />
                        {t('stress.errorMessage2')}
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button variant="contained" onClick={() => setOpenErrorDialog(false)}>
                        {t('stress.close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </MainCard>
    );
};

export default CreateStressTest;
